import React from "react";
import {
  SEO,
  PageLayout,
  Section,
  AnimatedSelector,
  BackgroundImages,
  FishermanHeader,
  MarkdownContent,
  WebsiteComponentInteractions,
  OutboundLink,
  GroupedList,
  MultipleComponentIterator,
  FadeReveal,
  FullImageCard,
  InternalLink,
  BasicCarousel,
  componentIterator,
  Image,
  TeamMemberV2,
} from "@bluefin/components";
import { Button, Grid, Card } from "semantic-ui-react";
import { graphql } from "gatsby";

import WebsiteLayout from "../components/WebsiteLayout.jsx";
import {
  getComponentFiles,
  getComponentContentNodeContent,
  getComponentInteractions,
} from "../utils/utils";
import { createTeamMemberSlug } from "@bluefin/gatsby-plugin-fisherman-page-creator";

export default class TeamPage extends React.PureComponent {
  render() {
    const { data } = this.props;
    const {
      fishermanBusiness,
      fishermanBusinessWebsitePage,
      bookingUrl,
      allFishermanBusinessTeam,
      allFishermanBusinessTeamGroup,
    } = data;

    return (
      <WebsiteLayout>
        <SEO
          businessName={fishermanBusiness.name}
          pageName={fishermanBusinessWebsitePage.title}
          description={fishermanBusinessWebsitePage.description}
          images={getComponentFiles({
            components: fishermanBusinessWebsitePage.components,
            componentName: "HeroElement",
            numToSelect: 1,
          })}
          title={fishermanBusinessWebsitePage.seoTitle}
          ogUrl={"https://www.brassfields.com/team/"}
        />
        <PageLayout hero={false} className={"team-page"}>
          <Section className={"default-hero-section"}>
            <AnimatedSelector
              targetSelector={
                ".image.background::before, .image.background::after"
              }
              duration={1500}
              transitionType={"zoom"}
              transitionProps={{
                animating: { scale: 1 },
                preAnimating: { scale: 1.4 },
                serverSide: { scale: 1.4 },
              }}
              easing={"cubic-bezier(0,1,0.99,1)"}
              runPolicy={"RUN_ONCE_EVERY_VISIT"}
              animationKey={"team-hero-animation"}
            >
              <BackgroundImages
                images={getComponentFiles({
                  components: fishermanBusinessWebsitePage.components,
                  componentName: "HeroElement",
                })}
                carouselProps={{
                  leftArrowIcon: "arrow-left",
                  rightArrowIcon: "arrow-right",
                }}
                imageProps={{ loadStrategy: "eager" }}
              >
                <div className={"content-container"}>
                  <FishermanHeader
                    as={"h1"}
                    content={getComponentContentNodeContent({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "team_hero_header",
                      defaultValue: "Meet Our Team",
                    })}
                  />
                  <MarkdownContent
                    content={getComponentContentNodeContent({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "team_hero_description",
                    })}
                  />
                  <WebsiteComponentInteractions
                    alignment={"center"}
                    ctas={getComponentInteractions({
                      components: fishermanBusinessWebsitePage.components,
                      componentId: "team_hero_description",
                    })}
                    fallback={
                      <React.Fragment>
                        <Button
                          primary={true}
                          as={OutboundLink}
                          to={bookingUrl.url}
                          event={{
                            category: "Booking",
                            action: "Schedule Appointment Intent",
                          }}
                          content={bookingUrl.title}
                        />
                      </React.Fragment>
                    }
                  />
                </div>
              </BackgroundImages>
            </AnimatedSelector>
          </Section>
          <Section className={"team-section"}>
            <GroupedList
              items={allFishermanBusinessTeam.nodes}
              groupBy={"teams.name"}
              childItemsKey={"iterator"}
              defaultGroupName={"Our Team"}
              groupOrderOptions={{
                defaultGroupPosition: "start",
                orderKey: "name",
                order: allFishermanBusinessTeamGroup.nodes,
              }}
              itemComponent={
                <MultipleComponentIterator
                  components={[
                    {
                      component: (
                        <FadeReveal className={"ui card"} triggerOnce={true} />
                      ),
                      propMap: { key: "_id" },
                      children: [
                        {
                          component: (
                            <FullImageCard
                              link={true}
                              as={InternalLink}
                              event={{
                                category: "Team",
                                action: "View Team Member",
                              }}
                            />
                          ),
                          propMap: {
                            key: "_id",
                            image: "gatsbyImage",
                            to: createTeamMemberSlug,
                          },
                          children: [
                            {
                              component: <TeamMemberV2.Name />,
                              propMap: { name: "name" },
                            },
                            {
                              component: <TeamMemberV2.Role />,
                              propMap: { role: "role" },
                            },
                          ],
                        },
                      ],
                    },
                  ]}
                />
              }
              groupHeaderComponent={<FishermanHeader as={"h2"} />}
              groupContainerComponent={
                <Card.Group itemsPerRow={4} className={"team-members"} />
              }
              groupDescriptionKey={"description"}
            />
          </Section>
          <Section className={"book-now-section"}>
            <FishermanHeader
              as={"h2"}
              content={getComponentContentNodeContent({
                components: fishermanBusinessWebsitePage.components,
                componentId: "book_now_section_header",
                defaultValue: "Not sure who to book with?",
              })}
            />
            <Grid stackable={true}>
              <Grid.Column width={8} className={"images"}>
                <FadeReveal duration={1250} triggerOnce={true} delay={0}>
                  <BasicCarousel
                    size={1}
                    slide={true}
                    displayIndicators={false}
                    displayPlayPauseButton={true}
                    autoScroll={false}
                    leftArrowIcon={"arrow-left"}
                    rightArrowIcon={"arrow-right"}
                    mobile={{ size: 1 }}
                    items={componentIterator({
                      iterator: getComponentFiles({
                        components: fishermanBusinessWebsitePage.components,
                        componentIdentifier: "book_now_section_images",
                      }),
                      component: <Image background={true} />,
                      propMap: { src: "__all__" },
                    })}
                  />
                </FadeReveal>
              </Grid.Column>
              <Grid.Column width={8} className={"content"}>
                <FadeReveal duration={1250} triggerOnce={true} delay={450}>
                  <div>
                    <MarkdownContent
                      content={getComponentContentNodeContent({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "book_now_section_description",
                      })}
                    />
                    <WebsiteComponentInteractions
                      ctas={getComponentInteractions({
                        components: fishermanBusinessWebsitePage.components,
                        componentId: "book_now_section_description",
                      })}
                      fallback={
                        <React.Fragment>
                          <Button
                            primary={true}
                            as={OutboundLink}
                            to={bookingUrl.url}
                            event={{
                              category: "Booking",
                              action: "Schedule Appointment Intent",
                            }}
                            content={bookingUrl.title}
                          />
                        </React.Fragment>
                      }
                    />
                  </div>
                </FadeReveal>
              </Grid.Column>
            </Grid>
          </Section>
        </PageLayout>
      </WebsiteLayout>
    );
  }
}

export const query = graphql`
  query {
    fishermanBusiness {
      name
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Team" }) {
      title
      description
      components {
        fastId
        componentIdentifier
        order
        files {
          file
          altText
          gatsbyFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        contentNodes {
          content
        }
        interactions {
          _id
          order
          behaviorType
          displayType
          enabled
          label
          style
          url
          role
        }
      }
      seoTitle
    }
    bookingUrl: fishermanBusinessWebsitePage(
      title: { regex: "/Book Now|Make An Appointment/" }
      pageType: { eq: "External" }
    ) {
      url
      title
    }
    allFishermanBusinessTeam(
      sort: { order: ASC, fields: order }
      filter: { show: { eq: true } }
    ) {
      nodes {
        _id
        name
        role
        group
        email
        phone
        description
        image
        gatsbyImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        socialMedia {
          platform
          url
        }
        hours {
          startDay
          openTime
          closeTime
        }
        teams {
          _id
          name
          description
        }
        locations {
          _id
          name
        }
        show
      }
    }
    allFishermanBusinessTeamGroup(sort: { order: ASC, fields: order }) {
      nodes {
        name
      }
    }
  }
`;
